// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import _ from "lodash";
import { Box } from "@mui/material";
import { useMyHrFilterChange } from "my-hr/hooks";
import { MY_LEAVE_GET_EXAMINERS_NAME, MY_LEAVE_GET_ASSIGNERS_NAME } from "my-leave/graphql";
import { MY_NEWS_GET_EDITORS_NAME } from "my-news/graphql/queries";
import {
  OcConfirmationDialog,
  OcErrorBoundary,
  OcRoleFilter,
} from "oc/components";
import { useOcClientState, useOcIsMobile, useOcPolling } from "oc/hooks";

type RoleType = {
  product: string;
  role: string;
  contract?: string;
  roleId?: string;
  roleFilters?: Record<string, any>;
  [key: string]: any;
};

type UnsavedRoleType = {
  roles: RoleType[];
};

type MyHrRoleCollapseProps = {
  role: RoleType;
  index: number;
  roleIndex: number;
  onUpdateRole: (role: RoleType) => void;
  onRemoveRole: (role: RoleType) => void;
  unsavedRole: UnsavedRoleType;
  showUnsavedRole: boolean;
  onAddUnsavedRole: (role: RoleType) => void;
  onRemoveUnsavedRole: (role: RoleType) => void;
  needToSave: boolean;
  skipInitTempState?: boolean;
  editable?: boolean;
  product: string;
};

export const MyHrRoleCollapse = ({
  role,
  index,
  roleIndex,
  onUpdateRole,
  onRemoveRole,
  unsavedRole,
  showUnsavedRole,
  onAddUnsavedRole,
  onRemoveUnsavedRole,
  needToSave,
  skipInitTempState,
  product,
}: MyHrRoleCollapseProps) => {
  const { t, i18n } = useTranslation();
  const isMobile = useOcIsMobile();

  // Fetch examiners for "user" roles (except MyCafeteria)
  const shouldFetchExaminers = role.role === "user" && role.product !== "MyCafeteria";

  const { data } = useQuery(MY_LEAVE_GET_EXAMINERS_NAME, {
    fetchPolicy: "cache-and-network",
    pollInterval: useOcPolling(1000 * 60),
    variables: {
      contract: role?.contract ?? "",
      product: role.product,
      locale: i18n.language || "hu",
    },
    skip: !shouldFetchExaminers,
  });

  const examinersNames = shouldFetchExaminers ? (data?.getExaminersName || []) : undefined;

  // Fetch editors for "user" roles in MyNews
  const shouldFetchEditors = role.role === "user" && role.product === "MyNews";

  const { data: editorsData } = useQuery(MY_NEWS_GET_EDITORS_NAME, {
    fetchPolicy: "cache-and-network",
    pollInterval: useOcPolling(1000 * 60),
    variables: {
      contract: role?.contract ?? "",
      locale: i18n.language || "hu",
    },
    skip: !shouldFetchEditors,
  });

  const editorsNames = shouldFetchEditors ? (editorsData?.getEditorsName || []) : undefined;

  // Fetch assigners for "user" roles in MyLeave
  const shouldFetchAssigners = role.role === "user" && role.product === "MyLeave";

  const { data: assignersData } = useQuery(MY_LEAVE_GET_ASSIGNERS_NAME, {
    fetchPolicy: "cache-and-network",
    pollInterval: useOcPolling(1000 * 60),
    variables: {
      contract: role?.contract ?? "",
      product: role.product,
      locale: i18n.language || "hu",
    },
    skip: !shouldFetchAssigners,
  });

  const assignersNames = shouldFetchAssigners ? (assignersData?.getAssignersName || []) : undefined;

  let tempStateId =
    "showCollapse" +
    role?.product +
    role?.role +
    role?.contract +
    index +
    "tempState";

  const [tempState, setTempState] = useOcClientState(tempStateId, role);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleRestore = () => {
    setTempState(role);
    onRemoveUnsavedRole(role);
  };

  useEffect(() => {
    if (!tempState && role) {
      setTempState(role);
    }
    //if (roleIndex === 0) {
    // setShowCollapse(true);
    //}
    // eslint-disable-next-line
  }, []);

  const handleRemoveRole = () => {
    setTempState(role);
    onRemoveRole(tempState);
  };

  let isMyHRUser = role?.product === "MyHR" && role?.role === "user";

  let isInUnsavedRole =
    unsavedRole.roles.filter(
      (item) =>
        item.contract === tempState.contract &&
        item.role === tempState.role &&
        item.product === tempState.product,
    ).length > 0
      ? true
      : false;

  let showBorder = isInUnsavedRole && showUnsavedRole;

  if (!_.isEqual(role, tempState) && !skipInitTempState) {
    if (!isInUnsavedRole) {
      onAddUnsavedRole(tempState);
    }
  }

  let isExact =
    role?.product === tempState?.product &&
    role?.contract === tempState?.contract &&
    role.role === tempState?.role &&
    _.isEqual(role?.roleFilters, tempState?.roleFilters);

  const handleSaveRole = (showDialog = true, isInUnsavedRole = false) => {
    let newState = _.cloneDeep(tempState);
    newState.roleId = role.roleId;

    const isEmptyFilters = Object.entries(tempState?.roleFilters || {})
      .filter(([key]) => key !== "__typename")
      .every(([, filter]) => Array.isArray(filter) && filter.length === 0);

    if (
      ["examiner", "viewer", "uploader", "assigner"].includes(role.role) &&
      showDialog
    ) {
      if (isEmptyFilters || isInUnsavedRole) {
        setShowConfirmDialog(true);
        return;
      }
    }

    onUpdateRole(newState);
    onRemoveUnsavedRole(tempState);
  };

  const handleDialogConfirm = () => {
    setShowConfirmDialog(false);
    handleSaveRole(false);
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const noFilters = role.role === "admin" || role.role === "user";

  const { handleAddFilter, handleRemoveFilter } = useMyHrFilterChange({
    role: tempState,
    onUpdateRole: setTempState,
  });

  // MyHR user szerepnél ne legyen törlés gomb
  let buttonBarProps = isMyHRUser ? {} : {
    rightButtonText: t("OC_DELETE"),
    rightButtonColor: "primary",
    handleRightButton: handleRemoveRole,
    handlePrimaryButton: handleSaveRole,
    primaryButtonText: needToSave ? t("OC_SAVE") : "",
  };

  // TODO f clean up
  if (!noFilters)
    buttonBarProps = {
      handlePrimaryButton: handleSaveRole,
      primaryButtonText: t("OC_SAVE"),
      primaryButtonDisabled: needToSave || !role.roleId ? false : isExact,
      primaryButtonId: "save-button",
      handleRightButton: isExact ? handleRemoveRole : handleRestore,
      rightButtonText: isExact ? t("OC_DELETE") : t("OC_RESET"),
      rightButtonColor: isExact ? "primary" : "secondary",
      handleLeftButton: !isExact ? handleRemoveRole : undefined,
      leftButtonColor: !isExact ? "primary" : undefined,
      leftButtonText: !isExact ? t("OC_DELETE") : undefined,
      rightButtonId: "delete-button",
    };

  return (
    <OcErrorBoundary>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleDialogConfirm}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("MY_HR_ADD_ALL_PERMISSIONS_DIALOG_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <Box padding="0px" width={isMobile ? "100%" : "728px"}>
        <OcRoleFilter
          subtypeProps={{
            queryType: "admin",
            handleAddItem: handleAddFilter,
            handleRemoveItem: handleRemoveFilter,
            roleFilters: tempState.roleFilters,
            role: role.role,
            product: product,
          }}
          innerWidth="100%"
          noFilters={noFilters}
          buttonBarProps={{
            noLine: true,
            paddingTop: "10px",
            ...buttonBarProps,
          }}
          chipData={{
            role: role.role,
            product,
          }}
          error={showBorder}
          defaultOpened={roleIndex === 0}
          examinersNames={examinersNames}
          editorsNames={editorsNames}
          assignersNames={assignersNames}
        />
      </Box>
    </OcErrorBoundary>
  );
};

export default MyHrRoleCollapse;
